import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthUser } from '../../../../services/auth/authService';
import { UserAvatar } from './UserAvatar';

export interface UserMenuProps {
  user: AuthUser;
  systemRole: string | null;
  isAdmin?: boolean;
  onSignOut: () => void;
  className?: string;
}

/**
 * Format role for display (e.g., ADMIN -> Platform Admin, SALES_MANAGER -> Sales Manager)
 */
const formatRole = (role: string | null): string => {
  if (!role) return 'User';
  if (role === 'ADMIN') return 'Platform Admin';
  return role
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * UserMenu component displays user avatar, name, role badge with a dropdown menu.
 * Dropdown contains options for profile, admin (if applicable), and sign out.
 */
export const UserMenu: React.FC<UserMenuProps> = ({
  user,
  systemRole,
  isAdmin = false,
  onSignOut,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Get display name (prefer displayName, fallback to email username)
  const displayName = user.displayName || user.email?.split('@')[0] || 'User';
  const formattedRole = formatRole(systemRole);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleProfileClick = () => {
    setIsOpen(false);
    navigate('/profile');
  };

  const handleAdminClick = () => {
    setIsOpen(false);
    navigate('/admin');
  };

  const handleSignOut = () => {
    setIsOpen(false);
    onSignOut();
  };

  return (
    <div ref={menuRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        onClick={handleToggle}
        className="
          flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
          hover:bg-secondary-50 dark:hover:bg-secondary-800
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
          dark:focus:ring-offset-secondary-900
        "
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`User menu for ${displayName}`}
      >
        {/* Avatar */}
        <UserAvatar name={displayName} imageUrl={user.photoURL} size="md" />

        {/* Name and Role */}
        <div className="flex flex-col items-start">
          <span className="text-sm font-medium text-secondary-900 dark:text-white leading-tight capitalize">
            {displayName}
          </span>
          <span className="text-xs text-secondary-500 dark:text-secondary-400">
            {formattedRole}
          </span>
        </div>

        {/* Dropdown Arrow */}
        <svg
          className={`w-4 h-4 text-secondary-500 dark:text-secondary-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="
            absolute right-0 mt-2 w-48 py-1 rounded-lg shadow-lg
            bg-white dark:bg-secondary-800
            border border-secondary-200 dark:border-secondary-700
            z-50
          "
          role="menu"
          aria-orientation="vertical"
        >
          {/* Update Profile */}
          <button
            onClick={handleProfileClick}
            className="
              w-full px-4 py-2 text-left text-sm transition-colors
              text-secondary-700 dark:text-secondary-200
              hover:bg-secondary-100 dark:hover:bg-secondary-700
              focus:outline-none focus:bg-secondary-100 dark:focus:bg-secondary-700
            "
            role="menuitem"
          >
            Update Profile
          </button>

          {/* Admin - only shown for admins */}
          {isAdmin && (
            <button
              onClick={handleAdminClick}
              className="
                w-full px-4 py-2 text-left text-sm transition-colors
                text-secondary-700 dark:text-secondary-200
                hover:bg-secondary-100 dark:hover:bg-secondary-700
                focus:outline-none focus:bg-secondary-100 dark:focus:bg-secondary-700
              "
              role="menuitem"
            >
              Admin
            </button>
          )}

          {/* Divider */}
          <div className="my-1 border-t border-secondary-200 dark:border-secondary-700" />

          {/* Sign Out */}
          <button
            onClick={handleSignOut}
            className="
              w-full px-4 py-2 text-left text-sm transition-colors
              text-secondary-700 dark:text-secondary-200
              hover:bg-secondary-100 dark:hover:bg-secondary-700
              focus:outline-none focus:bg-secondary-100 dark:focus:bg-secondary-700
            "
            role="menuitem"
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
